import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Sparkles, ArrowRight } from "lucide-react";
import { useFeatures, PLANS, ADDONS } from "../../util/admin/features";
import { spring } from "../../util/shared/motion";

// Presentatie-info per afgeschermde feature.
const GATE_CONFIG = {
   reservations: {
      title: "Reservaties zit in Pro",
      description:
         "Laat gasten online een tafel reserveren, beheer je capaciteit en verstuur automatische herinneringen. Upgrade naar Pro om reservaties te activeren.",
      badge: `Inbegrepen in Pro · €${PLANS.pro.price}/mnd`,
   },
   giftCards: {
      title: "Cadeaubonnen is een add-on",
      description:
         "Verkoop digitale cadeaubonnen in jouw webshop en laat klanten elkaar trakteren. Voeg de Cadeaubonnen-add-on toe aan je abonnement om te starten.",
      badge: `Add-on · +€${ADDONS.giftCards.price}/mnd`,
   },
};

const FeatureLocked = ({ feature }) => {
   const navigate = useNavigate();
   const config = GATE_CONFIG[feature] || {
      title: "Niet beschikbaar in jouw abonnement",
      description: "Upgrade je abonnement om deze functie te ontgrendelen.",
      badge: "Upgrade vereist",
   };

   return (
      <div className="flex items-center justify-center min-h-[70vh] px-4">
         <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={spring}
            className="relative max-w-lg w-full text-center bg-white rounded-3xl border border-gray-100 shadow-[0_20px_60px_-20px_rgba(37,99,235,0.35)] px-8 py-12 overflow-hidden"
         >
            {/* decoratieve gloed */}
            <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-blue-500/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-blue-500/10 blur-3xl" />

            <div className="relative">
               <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-500">
                  <Lock size={28} />
               </div>

               <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-3 py-1 text-xs font-semibold text-blue-600">
                  <Sparkles size={13} /> {config.badge}
               </span>

               <h2 className="mt-4 text-2xl font-bold text-gray-900">{config.title}</h2>
               <p className="mt-3 text-gray-500 leading-relaxed">{config.description}</p>

               <button
                  onClick={() => navigate("/dashboard/abonnement")}
                  className="mt-8 inline-flex items-center gap-2 rounded-xl bg-blue-500 px-6 py-3 font-semibold text-white transition-all hover:bg-blue-600 hover:gap-3 active:scale-[0.98]"
               >
                  Bekijk abonnementen
                  <ArrowRight size={18} />
               </button>
            </div>
         </motion.div>
      </div>
   );
};

// Wrapper rond een pagina/route. Toont de kinderen als de feature beschikbaar is,
// een laadstatus tijdens het ophalen, en anders een upgrade-scherm.
const FeatureGate = ({ feature, children }) => {
   const { features, isLoading } = useFeatures();

   if (isLoading) {
      return (
         <div className="flex items-center justify-center min-h-[70vh]">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500/20 border-t-blue-500" />
         </div>
      );
   }

   if (!features[feature]) {
      return <FeatureLocked feature={feature} />;
   }

   return children;
};

export default FeatureGate;
